/**
 * What a business register may change about an organisation (#2267 port 1).
 *
 * Pure, and its own file, because this is the whole decision the lookup makes.
 * The dialog owns searching and presenting; `useSaveEntity` owns the write; what
 * is left is "which of this record's fields does an external register get to
 * overwrite, and which belong to the person who typed them".
 *
 * The answer is the legacy page's, preserved deliberately rather than
 * re-derived:
 *
 *   · **legal identity FOLLOWS the register.** `legal_name`, and the VAT number
 *     and country where the hit carries one, are overwritten. That is the point
 *     of adopting a registry hit — a legal name that disagrees with the register
 *     is wrong, not a preference.
 *   · **`website` is filled, never replaced.** A register's website field is
 *     frequently stale or a holding page, and somebody who typed one knows
 *     something the register does not.
 *   · **`display_name` is never touched**, and this is the quiet one. It is what
 *     every list, lookup and palette in the console shows, and it is often
 *     deliberately shorter than the legal name ("Moonbase", not "Moonbase BV").
 *     Overwriting it would rename the organisation everywhere from a dialog
 *     whose title says "Look up company".
 *
 * ── the VAT number keeps its prefix, on purpose ─────────────────────────────
 * A register returns `BE1039343221`, and that whole string goes in
 * `vat_number` while `vat_country` gets `BE`. It looks like duplication and is
 * not: `checkVat` strips a leading two-letter prefix before calling VIES, so the
 * prefixed form is what the verification path already expects, and the separate
 * country is what `billing.vat_treatment` reads. Splitting them here would make
 * the stored number disagree with every register that issued it.
 */
import type { Row } from '@solum/sdk'
import type { CompanyHit } from './companyLookup'

/**
 * The patch a registry hit produces for this row.
 *
 * `current` is the record as it stands — read only to decide whether a field is
 * already answered, never merged into the result. A patch that echoed unchanged
 * values back would make every adoption look like an edit to every column.
 */
export function companyAdoptionPatch(hit: CompanyHit, current: Row): Record<string, unknown> {
  const patch: Record<string, unknown> = { legal_name: hit.name }
  if (hit.vat) {
    patch.vat_number = hit.vat
    patch.vat_country = hit.country
  }
  /* Filled, not replaced. An empty string counts as unanswered — a cleared text
     input is stored as `''` by the form, not as null, and treating that as "the
     person chose no website" is what makes the register's value unreachable
     forever after one stray keystroke. */
  const website = current.website == null ? '' : String(current.website).trim()
  if (hit.website && website === '') patch.website = hit.website
  return patch
}
